/**
 * MicroAppDock — Accept gate (B1) + Cap'n Web MicroAppPanel for the chat right-side slot.
 * Used when a micro-app is opened beside Ros (finder Open, or Ros open_micro_app tool) or in the
 * full-page gadget viewer. Accept collects optional Jira connector config (mock / live / off) so
 * LOADER can inject this.env.JIRA without putting credentials in the gadget package.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import { MicroAppPanel } from './MicroAppPanel'

type JiraMode = 'off' | 'mock' | 'live'

interface JiraConnectorStatus {
  mode: JiraMode
  projectKey: string | null
  hasCredentials: boolean
}

export interface MicroAppDockMeta {
  id: string
  version: string
  displayName: string
  accepted: boolean
  /** App Artifacts are accepted first, then become runnable only after install() finishes. */
  ready?: boolean
  runtime?: string
  artifactKind?: 'app-artifact' | null
  artifactId?: string | null
  artifactVersion?: number | null
  connectors?: Array<{ name: string; type: string; required?: boolean; description?: string }>
  acceptedMeta?: {
    acceptedAt: string
    codeVersion: string
    connectors?: {
      JIRA?: JiraConnectorStatus
    }
  } | null
}

export interface MicroAppDockProps {
  definitionId: string
  artifactVersion?: number
  displayName?: string
  apiBaseUrl: string
  token: string | null
  /** Artifact renderers prepare their trusted, connector-free package without a separate prompt. */
  autoPrepare?: boolean
  /** Bubble title/version up so the panel chrome can show them. */
  onMeta?: (meta: MicroAppDockMeta) => void
}

function authHeaders(token: string | null, json = false): Record<string, string> {
  const headers: Record<string, string> = {}
  if (json) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

export function MicroAppDock({ definitionId, artifactVersion, displayName, apiBaseUrl, token, autoPrepare = false, onMeta }: MicroAppDockProps) {
  const [meta, setMeta] = useState<MicroAppDockMeta | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)

  const [jiraMode, setJiraMode] = useState<JiraMode>('mock')
  const [projectKey, setProjectKey] = useState('DEMO')
  const [baseUrl, setBaseUrl] = useState('')
  const [boardId, setBoardId] = useState('')
  const [email, setEmail] = useState('')
  const [apiToken, setApiToken] = useState('')
  const [showJiraReconfig, setShowJiraReconfig] = useState(false)

  const tokenRef = useRef(token)
  tokenRef.current = token
  const onMetaRef = useRef(onMeta)
  onMetaRef.current = onMeta
  const prepareAbortRef = useRef<AbortController | null>(null)
  const versionQuery = artifactVersion ? `?artifactVersion=${artifactVersion}` : ''

  useEffect(() => () => {
    const active = prepareAbortRef.current
    prepareAbortRef.current = null
    active?.abort()
  }, [definitionId, artifactVersion])

  const hasJiraConnector = useMemo(
    () => (meta?.connectors || []).some((c) => c.type === 'jira' || c.name === 'JIRA' || c.name === 'JIRA_WRITE'),
    [meta],
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await fetch(
          `${apiBaseUrl}/api/micro-apps/${encodeURIComponent(definitionId)}/meta${versionQuery}`,
          { headers: authHeaders(tokenRef.current), credentials: 'include' },
        )
        const data = await res.json().catch(() => ({})) as MicroAppDockMeta & { error?: string }
        if (!res.ok) throw new Error(data.error || `Failed to load meta (${res.status})`)
        if (!cancelled) {
          setMeta(data)
          const acceptedJira = data.acceptedMeta?.connectors?.JIRA
          if (acceptedJira) {
            setJiraMode(acceptedJira.mode)
            setProjectKey(acceptedJira.projectKey || (acceptedJira.mode === 'mock' ? 'DEMO' : ''))
          }
          onMetaRef.current?.(data)
        }
      } catch (err) {
        if (!cancelled) {
          setMeta(null)
          setError(err instanceof Error ? err.message : String(err))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
    // Token renewals must not re-fetch meta / remount the panel (memory).
  }, [definitionId, artifactVersion, apiBaseUrl, versionQuery])

  const acceptApp = useCallback(async () => {
    prepareAbortRef.current?.abort()
    const controller = new AbortController()
    prepareAbortRef.current = controller
    setAccepting(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {}
      if (hasJiraConnector) {
        if (jiraMode === 'off') {
          body.connectors = { JIRA: { mode: 'off' } }
        } else if (jiraMode === 'mock') {
          body.connectors = {
            JIRA: {
              mode: 'mock',
              projectKey: projectKey.trim() || 'DEMO',
            },
          }
        } else {
          body.connectors = {
            JIRA: {
              mode: 'live',
              projectKey: projectKey.trim(),
              baseUrl: baseUrl.trim(),
              boardId: boardId.trim() || undefined,
              email: email.trim(),
              apiToken: apiToken.trim(),
            },
          }
        }
      }

      const res = await fetch(
        `${apiBaseUrl}/api/micro-apps/${encodeURIComponent(definitionId)}/accept${versionQuery}`,
        {
          method: 'POST',
          headers: authHeaders(tokenRef.current, true),
          credentials: 'include',
          body: JSON.stringify(body),
          signal: controller.signal,
        },
      )
      const data = await res.json().catch(() => ({})) as {
        ok?: boolean
        error?: string
        ready?: boolean
        connectors?: { JIRA?: JiraConnectorStatus }
      }
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || `Accept failed (${res.status})`)
      }
      if (controller.signal.aborted) return
      let ready = data.ready !== false
      if (!ready) {
        for (let attempt = 0; attempt < 120 && !ready && !controller.signal.aborted; attempt++) {
          await new Promise((resolve) => window.setTimeout(resolve, 500))
          if (controller.signal.aborted) return
          const statusRes = await fetch(
            `${apiBaseUrl}/api/micro-apps/${encodeURIComponent(definitionId)}/meta${versionQuery}`,
            {
              headers: authHeaders(tokenRef.current),
              credentials: 'include',
              signal: controller.signal,
            },
          )
          const status = await statusRes.json().catch(() => ({})) as {
            ready?: boolean
            error?: string
          }
          if (!statusRes.ok) {
            throw new Error(status.error || `Install status failed (${statusRes.status})`)
          }
          ready = status.ready === true
        }
        if (controller.signal.aborted) return
        if (!ready) throw new Error('App installation is taking longer than expected. Try again shortly.')
      }
      const nextAcceptedMeta = {
        acceptedAt: new Date().toISOString(),
        codeVersion: meta?.version || '',
        connectors: data.connectors,
      }
      const next: MicroAppDockMeta = meta
        ? { ...meta, accepted: true, ready, acceptedMeta: nextAcceptedMeta }
        : {
            id: definitionId,
            version: '',
            displayName: displayName || definitionId,
            accepted: true,
            ready,
            acceptedMeta: nextAcceptedMeta,
          }
      setMeta(next)
      onMeta?.(next)
      setShowJiraReconfig(false)
    } catch (err) {
      if (controller.signal.aborted) return
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      if (prepareAbortRef.current === controller) {
        prepareAbortRef.current = null
        setAccepting(false)
      }
    }
  }, [
    definitionId,
    displayName,
    apiBaseUrl,
    artifactVersion,
    versionQuery,
    meta,
    onMeta,
    hasJiraConnector,
    jiraMode,
    projectKey,
    baseUrl,
    boardId,
    email,
    apiToken,
  ])

  useEffect(() => {
    if (
      !autoPrepare ||
      !meta ||
      meta.artifactKind !== 'app-artifact' ||
      (meta.accepted && meta.ready !== false) ||
      accepting ||
      error
    ) return
    void acceptApp()
  }, [autoPrepare, meta, accepting, error, acceptApp])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 3 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading app…</Typography>
      </Box>
    )
  }

  if (error && !meta) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
  }

  const accepted = !!meta?.accepted && meta?.ready !== false
  const title = meta?.displayName || displayName || definitionId
  const connectors = meta?.connectors || []

  if (!accepted && autoPrepare && meta?.artifactKind === 'app-artifact') {
    return (
      <Box sx={{ p: 3 }}>
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={() => { setError(null); void acceptApp() }} disabled={accepting}>
              Try again
            </Button>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Preparing App Artifact…</Typography>
          </Box>
        )}
      </Box>
    )
  }

  // Allow re-running Accept to attach Jira after a prior Accept without it.
  if (!accepted || showJiraReconfig) {
    const liveIncomplete =
      hasJiraConnector &&
      jiraMode === 'live' &&
      (!projectKey.trim() || !baseUrl.trim() || !email.trim() || !apiToken.trim())

    return (
      <Box sx={{ p: 2.5, overflow: 'auto', height: '100%' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          Accept {title}?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {meta?.artifactKind === 'app-artifact' ? 'Prepares this App Artifact' : 'Creates your private extension'} ({definitionId}
          {meta?.version ? ` · v${meta.version}` : ''}). Client code runs in a sandboxed iframe;
          server methods run on a dynamic worker Facet.
        </Typography>
        {connectors.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {connectors.map(c => (
              <Chip
                key={c.name}
                size="small"
                label={c.required ? `${c.name} (required)` : c.name}
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        {hasJiraConnector && (
          <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Jira connector</FormLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Optional. Credentials stay on the platform binding; the package only sees getMeta / createIssues.
              </Typography>
              <RadioGroup
                value={jiraMode}
                onChange={(e) => {
                  const nextMode = e.target.value as JiraMode
                  if (nextMode === 'live' && jiraMode === 'mock' && projectKey.trim() === 'DEMO') {
                    setProjectKey('')
                  } else if (nextMode === 'mock' && !projectKey.trim()) {
                    setProjectKey('DEMO')
                  }
                  setJiraMode(nextMode)
                }}
              >
                <FormControlLabel value="mock" control={<Radio size="small" />} label="Mock (DEMO-### keys, canned getMeta)" />
                <FormControlLabel value="live" control={<Radio size="small" />} label="Live Jira Cloud" />
                <FormControlLabel value="off" control={<Radio size="small" />} label="Skip Jira (review only)" />
              </RadioGroup>
            </FormControl>

            {(jiraMode === 'mock' || jiraMode === 'live') && (
              <TextField
                label="Project key"
                value={projectKey}
                onChange={(e) => setProjectKey(e.target.value)}
                size="small"
                fullWidth
                required={jiraMode === 'live'}
                sx={{ mt: 1.5 }}
                helperText={jiraMode === 'mock' ? 'Used as DEMO project prefix (default DEMO)' : 'Required for live creates'}
              />
            )}

            {jiraMode === 'live' && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                {accepted && meta?.acceptedMeta?.connectors?.JIRA?.hasCredentials && (
                  <Alert severity="info">
                    Live Jira is currently configured. Re-enter the site, email, and API token only
                    if you intend to replace that connector configuration.
                  </Alert>
                )}
                <TextField
                  label="Jira site URL"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  placeholder="your-site.atlassian.net"
                  helperText="With or without https://"
                />
                <TextField
                  label="Board id (optional)"
                  value={boardId}
                  onChange={(e) => setBoardId(e.target.value)}
                  size="small"
                  fullWidth
                  helperText="Needed for sprint prefill via Agile API"
                />
                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  fullWidth
                  required
                />
                <TextField
                  label="API token"
                  type="password"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  autoComplete="off"
                />
              </Stack>
            )}
          </Box>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={() => void acceptApp()}
            disabled={accepting || liveIncomplete}
          >
            {accepting ? 'Accepting…' : accepted ? 'Save & reopen' : 'Accept & open'}
          </Button>
          {accepted && (
            <Button variant="text" onClick={() => setShowJiraReconfig(false)} disabled={accepting}>
              Cancel
            </Button>
          )}
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
      {hasJiraConnector && (
        <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" onClick={() => setShowJiraReconfig(true)}>
            Configure Jira…
          </Button>
          <Typography variant="caption" color="text.secondary">
            {meta?.acceptedMeta?.connectors?.JIRA?.mode === 'live'
              ? `Live project: ${meta.acceptedMeta.connectors.JIRA.projectKey || 'not set'}`
              : meta?.acceptedMeta?.connectors?.JIRA?.mode === 'mock'
                ? `Mock project: ${meta.acceptedMeta.connectors.JIRA.projectKey || 'DEMO'}`
                : 'Jira off'}
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <MicroAppPanel
          definitionId={definitionId}
          artifactVersion={artifactVersion}
          apiBaseUrl={apiBaseUrl}
          token={token}
          isVisible
        />
      </Box>
    </Box>
  )
}
